import { apiClient } from "./api";

/**
 * Function to update the bond number and link it to a bond.
 *
 * @param {number} bondnoId - The ID of the bond number to update.
 * @param {number} bondId - The ID of the bond to link the bond number to.
 * @returns {Promise<Object>} - The response from the API containing updated bond and bond number details.
 * @throws {Error} - If the API call fails.
 */
export const updateBondNumber = async (bondnoId, bondId, usedBy) => {
  if (!bondnoId || !bondId) {
    throw new Error("Both bondnoId and bondId are required.");
  }

  try {
    const data = {
      bondno_id: bondnoId,
      bond_id: bondId,
      used_by: usedBy,
    };

    const response = await apiClient("/update-bond-number/", "PATCH", data);
    return response;
  } catch (error) {
    console.error("Error updating bond number:", error);
    throw error;
  }
};

/**
 * Function to calculate premium based on bond type and other parameters.
 *
 * @param {number} amount - Bond Amount
 * @param {string} dateOfBirth - Date of Birth in case of Guardianship Minor
 * @param {string} bondSubCategory - Sub Category of the Bond
 * @param {number} bondTerm - Term of the Bond
 * @returns {number} - Calculated Premium
 */
export const calculatePremium = (amount, dateOfBirth, bondSubCategory, bondTerm) => {
  if (bondSubCategory === "Guardian - Minor") {
    if (!amount || !dateOfBirth) return 0;
  
    amount = parseFloat(amount);
    if (isNaN(amount) || amount <= 0) {
      throw new Error("Invalid bond amount");
    }
  
    const effectiveDate = new Date();
    const dob = new Date(dateOfBirth);
    if (isNaN(dob.getTime())) {
      throw new Error("Invalid date of birth");
    }
  
    const turning18Date = new Date(
      dob.getFullYear() + 18,
      dob.getMonth(),
      dob.getDate()
    );
  
    let currentDateObj = new Date(effectiveDate);
    let yearIndex = 1;
  
    // how many whole years (minus the final partial) between eff. date and 18th birthday
    const timeTo18 = (turning18Date - currentDateObj) / (1000 * 60 * 60 * 24 * 365);
    const totalYears = Math.ceil(timeTo18) - 1;
  
    // multi-year discount from year 2 onward
    let discount = 0;
    if (totalYears >= 10) {
      discount = 0.30;
    } else if (totalYears >= 8) {
      discount = 0.25;
    } else if (totalYears >= 6) {
      discount = 0.20;
    } else if (totalYears >= 2) {
      discount = 0.15;
    }
  
    const slabs = [
      { limit: 2000, rate: 0.01 },
      { limit: 48000, rate: 0.005 },
      { limit: 150000, rate: 0.004 },
      { limit: 300000, rate: 0.0025 },
      { limit: 500000, rate: 0.001 },
    ];
  
    const totalPremiumByYear = [];
    let totalPremium = 0;
  
    while (currentDateObj < turning18Date) {
      const appliedDiscount = yearIndex === 1 ? 0 : discount;
  
      // 1) full-year premium by slab (discounted & rounded to cents)
      let remainingAmount = amount;
      let annualPremium = 0;
      const slabDetails = [];
  
      for (let { limit, rate } of slabs) {
        if (remainingAmount <= 0) break;
        const amt = Math.min(remainingAmount, limit);
        const premium = amt * rate;
        const discounted = Math.round(premium * (1 - appliedDiscount) * 100) / 100;
        annualPremium += discounted;
        slabDetails.push(
          `${amt} @ ${(rate * 100).toFixed(2)}% → $${premium.toFixed(2)} → ` +
          `disc ${(appliedDiscount * 100).toFixed(0)}% → $${discounted.toFixed(2)}`
        );
        remainingAmount -= amt;
      }
  
      // 2) detect & prorate final partial year
      const nextAnniversary = new Date(
        currentDateObj.getFullYear() + 1,
        currentDateObj.getMonth(),
        currentDateObj.getDate()
      );
      const isFinalYear = nextAnniversary > turning18Date;
  
      if (isFinalYear) {
        const msPerDay = 1000 * 60 * 60 * 24;
        const daysRemaining = Math.ceil((turning18Date - currentDateObj) / msPerDay);
        // Use a fixed 365-day year for proration to ignore leap years
        const daysInYear = 365;
        const factor = daysRemaining / daysInYear;
        annualPremium = Math.round(annualPremium * factor * 100) / 100;
      }
  
      // 3) round to nearest dollar & accumulate
      const annualRounded = Math.round(annualPremium);
      totalPremium += annualRounded;
  
      // 4) record this period’s details, ending on turning18Date only for final year
      const periodEnd = isFinalYear
        ? turning18Date
        : nextAnniversary;
  
      totalPremiumByYear.push({
        year: `${currentDateObj.toLocaleDateString()} – ${periodEnd.toLocaleDateString()}`,
        discountApplied: `${(appliedDiscount * 100).toFixed(0)}%`,
        slabBreakdown: slabDetails.join("; "),
        annualPremium: annualRounded,
        totalAccumulatedPremium: totalPremium,
      });
  
      // advance into next policy year (or exit if final)
      currentDateObj = isFinalYear ? turning18Date : nextAnniversary;
      yearIndex++;
    }
    return totalPremium;
  }
  
  

  // Blanket Bond flat rate
  if (bondSubCategory === "Blanket") {
    if (bondTerm === 1) return 175.0;
    if (bondTerm === 2) return 306.0;
    if (bondTerm === 3) return 437.0;
  }

  // Standard Slab Premium for other bond types
  let premium = 0;
  if (amount < 2000) premium += (amount * 10) / 1000;
  else {
    premium += (2000 * 10) / 1000;
    amount -= 2000;
    if (amount < 48000) premium += (amount * 5) / 1000;
    else {
      premium += (48000 * 5) / 1000;
      amount -= 48000;
      if (amount < 150000) premium += (amount * 4) / 1000;
      else {
        premium += (150000 * 4) / 1000;
        amount -= 150000;
        if (amount < 300000) premium += (amount * 2.5) / 1000;
        else {
          premium += (300000 * 2.5) / 1000;
          amount -= 300000;
          if (amount < 500000) premium += amount / 1000;
          else {
            premium += 500000 / 1000;
            amount -= 500000;
            premium += (amount * 0.5) / 1000;
          }
        }
      }
    }
  }

  return parseFloat(Math.max(premium, 100).toFixed(2));
};

/**
 * Guardianship premium — always pass ward DOB and bond subcategory.
 *
 * @param {number|string} bondAmount
 * @param {string} wardDateOfBirth
 * @param {string} bondSubCategory
 * @returns {number}
 */
export const calculateGuardianshipPremium = (
  bondAmount,
  wardDateOfBirth,
  bondSubCategory
) => {
  const amount =
    typeof bondAmount === "number"
      ? bondAmount
      : parseFloat(String(bondAmount ?? "").replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(amount)) return 0;
  return calculatePremium(amount, wardDateOfBirth, bondSubCategory);
};

/**
 * Formats a phone number string to XXX-XXX-XXXX, stripping country code if needed.
 *
 * @param {string} phoneNumber
 * @returns {string}
 */
export const removeCountryCode = (phoneNumber) => {
  let digits = phoneNumber.replace(/\D/g, "");
  if (digits.length > 10) {
    digits = digits.slice(-10);
  }
  return digits.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
};
